import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import invoiceAPI from '../../api/invoiceAPI';
import customerAPI from '../../api/customerAPI';
import productAPI from '../../api/productAPI';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';

const EditInvoicePage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [loadingInvoice, setLoadingInvoice] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    
    // Data states
    const [invoice, setInvoice] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    
    // Form state
    const [formData, setFormData] = useState({
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        customer: '',
        customerName: '',
        customerEmail: '',
        customerAddress: {},
        terms: 'net_30',
        customTerms: '',
        memo: '',
        lines: [
            { product: '', description: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 }
        ]
    });

    // Search states
    const [customerSearch, setCustomerSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');

    // New customer modal state
    const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
    const [newCustomerData, setNewCustomerData] = useState({
        name: '',
        email: '',
        phone: '',
        promoOptIn: {
            email: false,
            sms: false
        }
    });
    const [creatingCustomer, setCreatingCustomer] = useState(false);

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState({
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        referenceNumber: '',
        amount: 0,
        memo: ''
    });
    const [processingPayment, setProcessingPayment] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        if (invoice) {
            setFormData({
                invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                customer: invoice.customer?._id || invoice.customer || '',
                customerName: invoice.customerName || invoice.customer?.name || '',
                customerEmail: invoice.customerEmail || invoice.customer?.email || '',
                customerAddress: invoice.customerAddress || invoice.customer?.billingAddress || {},
                terms: invoice.terms || 'net_30',
                customTerms: invoice.customTerms || '',
                memo: invoice.memo || '',
                lines: invoice.lines?.map(line => ({
                    ...line,
                    product: line.product?._id || line.product || '',
                    description: line.description || '',
                    quantity: line.quantity || 0,
                    unitPrice: line.unitPrice || 0,
                    discount: line.discount || 0,
                    taxRate: line.taxRate || 0,
                    lineTotal: line.lineTotal || 0
                })) || []
            });

            if (invoice.amountDue) {
                setPaymentData(prev => ({
                    ...prev,
                    amount: invoice.amountDue
                }));
            }
        }
    }, [invoice]);

    // Calculate due date based on terms
    useEffect(() => {
        if (formData.terms && formData.invoiceDate && invoice?.status === 'draft') {
            const invoiceDate = new Date(formData.invoiceDate);
            let dueDate = new Date(invoiceDate);
            
            switch (formData.terms) {
                case 'due_on_receipt':
                    dueDate = invoiceDate;
                    break;
                case 'net_15':
                    dueDate.setDate(invoiceDate.getDate() + 15);
                    break;
                case 'net_30':
                    dueDate.setDate(invoiceDate.getDate() + 30);
                    break;
                case 'net_60':
                    dueDate.setDate(invoiceDate.getDate() + 60);
                    break;
                case '2_10_net_30':
                    dueDate.setDate(invoiceDate.getDate() + 30);
                    break;
                default:
                    dueDate.setDate(invoiceDate.getDate() + 30);
            }
            
            setFormData(prev => ({
                ...prev,
                dueDate: dueDate.toISOString().split('T')[0]
            }));
        }
    }, [formData.terms, formData.invoiceDate, invoice?.status]);

    const fetchInitialData = async () => {
        setLoadingInvoice(true);
        try {
            const [invoiceRes, customersRes, productsRes, accountsRes] = await Promise.all([
                invoiceAPI.getById(id),
                customerAPI.getAll(),
                productAPI.getAll(),
                chartOfAccountsAPI.getAll()
            ]);

            setInvoice(invoiceRes.data.data || invoiceRes);
            setCustomers(customersRes.data.data || []);
            setProducts(productsRes.data.data || []);
            setAccounts(accountsRes.data.data || []);
        } catch (err) {
            setError('Failed to load invoice data: ' + err.message);
        } finally {
            setLoadingInvoice(false);
        }
    };

    const filteredCustomers = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.companyName?.toLowerCase().includes(customerSearch.toLowerCase())
    );

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.description?.toLowerCase().includes(productSearch.toLowerCase())
    );

    // Check if form is read-only
    const isReadOnly = invoice?.status === 'paid' || invoice?.status === 'void';

    // Add New Customer Function
    const handleCreateCustomer = async () => {
        if (!newCustomerData.name.trim()) {
            setError('Customer name is required');
            return;
        }
    
        setCreatingCustomer(true);
        try {
            const response = await customerAPI.create(newCustomerData);
            
            let newCustomer;
            if (response.data) {
                newCustomer = response.data;
            } else if (response._id) {
                newCustomer = response;
            } else {
                throw new Error('Unexpected response format from server');
            }
            
            if (!newCustomer || !newCustomer._id) {
                throw new Error('Invalid customer data received from server');
            }
    
            const safeCustomer = {
                _id: newCustomer._id,
                name: newCustomer.name || newCustomerData.name,
                email: newCustomer.email || newCustomerData.email,
                phone: newCustomer.phone || newCustomerData.phone,
                promoOptIn: newCustomer.promoOptIn || newCustomerData.promoOptIn,
                ...newCustomer
            };
            
            setCustomers(prev => [safeCustomer, ...prev]);
            handleCustomerSelect(safeCustomer._id);
            
            setShowNewCustomerModal(false);
            setNewCustomerData({
                name: '',
                email: '',
                phone: '',
                promoOptIn: {
                    email: false,
                    sms: false
                }
            });
            
            setSuccess('Customer created successfully');
        } catch (err) {
            setError(err.message || 'Failed to create customer');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const handleCustomerSelect = (customerId) => {
        console.log(customerId);
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
            setFormData(prev => ({
                ...prev,
                customer: customerId,
                customerName: customer.name,
                customerEmail: customer.email,
                customerAddress: customer.billingAddress
            }));
            setCustomerSearch(customer.name);
        }
    };

    const addLine = () => {
        if (isReadOnly) return;
        setFormData({
            ...formData,
            lines: [
                ...formData.lines,
                { product: '', description: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 }
            ]
        });
    };

    const removeLine = (index) => {
        if (isReadOnly || formData.lines.length <= 1) return;
        const newLines = formData.lines.filter((_, i) => i !== index);
        setFormData({ ...formData, lines: newLines });
    };

    const updateLine = (index, field, value) => {
        if (isReadOnly) return;
        
        const newLines = [...formData.lines];
        
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxRate') {
            newLines[index][field] = parseFloat(value) || 0;
        } else {
            newLines[index][field] = value;
        }
    
        const quantity = parseFloat(newLines[index].quantity) || 0;
        const unitPrice = parseFloat(newLines[index].unitPrice) || 0;
        const discount = parseFloat(newLines[index].discount) || 0;
        
        const subtotal = quantity * unitPrice;
        const discountAmount = (subtotal * discount) / 100;
        newLines[index].lineTotal = parseFloat((subtotal - discountAmount).toFixed(2));
    
        setFormData({ ...formData, lines: newLines });
    };

    const calculateTotals = () => {
        const subtotal = formData.lines.reduce((sum, line) => sum + (parseFloat(line.lineTotal) || 0), 0);
        const discountTotal = formData.lines.reduce((sum, line) => {
            const lineSubtotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0);
            return sum + ((lineSubtotal * (parseFloat(line.discount) || 0)) / 100);
        }, 0);
        const taxTotal = formData.lines.reduce((sum, line) => {
            const taxableAmount = parseFloat(line.lineTotal) || 0;
            return sum + ((taxableAmount * (parseFloat(line.taxRate) || 0)) / 100);
        }, 0);
        const totalAmount = subtotal + taxTotal;
    
        return { 
            subtotal: parseFloat(subtotal.toFixed(2)), 
            discountTotal: parseFloat(discountTotal.toFixed(2)), 
            taxTotal: parseFloat(taxTotal.toFixed(2)), 
            totalAmount: parseFloat(totalAmount.toFixed(2)) 
        };
    };

    const handleSubmit = async (action) => {
        if (isReadOnly) return;
        
        const { subtotal, discountTotal, taxTotal, totalAmount } = calculateTotals();
        
        if (!formData.customer) {
            setError('Customer is required');
            return;
        }
    
        if (formData.lines.some(line => !line.product || line.quantity <= 0)) {
            setError('All lines must have a product and quantity greater than 0');
            return;
        }
    
        if (totalAmount <= 0) {
            setError('Invoice total must be greater than 0');
            return;
        }
    
        try {
            setLoading(true);
            setError('');
            setSuccess('');
    
            const payload = {
                invoiceDate: formData.invoiceDate,
                dueDate: formData.dueDate,
                customer: formData.customer,
                terms: formData.terms,
                memo: formData.memo || '',
                lines: formData.lines.map(line => ({
                    product: line.product,
                    description: line.description || '',
                    quantity: Number(line.quantity) || 0,
                    unitPrice: Number(line.unitPrice) || 0,
                    discount: Number(line.discount) || 0,
                    taxRate: Number(line.taxRate) || 0,
                    lineTotal: Number(line.lineTotal) || 0
                })),
                subtotal: Number(subtotal),
                discountTotal: Number(discountTotal),
                taxTotal: Number(taxTotal),
                totalAmount: Number(totalAmount),
                amountDue: Number(totalAmount)
            };
    
            if (formData.terms === 'custom' && formData.customTerms) {
                payload.customTerms = formData.customTerms;
            }
    
            if (action === 'update') {
                await invoiceAPI.update(id, payload);
                setSuccess('Invoice updated successfully');
                fetchInitialData(); // Refresh data
            } else if (action === 'updateAndSend') {
                await invoiceAPI.update(id, payload);
                const postResult = await invoiceAPI.postToAccounting(id);
                setSuccess(
                    `Invoice ${invoice.invoiceNumber} updated and posted to GL successfully.` +
                    (postResult?.data?.glBatchNumber ? ` Batch: ${postResult.data.glBatchNumber}` : '')
                );
                fetchInitialData();
            }
        } catch (err) {
            console.error('Error updating invoice:', err);
            if (err.response?.data?.errors) {
                const errorMessages = Object.values(err.response.data.errors).map(error => error.message).join(', ');
                setError(`Validation failed: ${errorMessages}`);
            } else {
                setError(err.message || 'Failed to update invoice');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsPaid = async () => {
        try {
            setProcessingPayment(true);
            setError('');
            
            // Create payment record and post to GL
            const response = await invoiceAPI.recordPayment(id, {
                paymentDate: paymentData.paymentDate,
                paymentMethod: paymentData.paymentMethod,
                referenceNumber: paymentData.referenceNumber,
                amount: paymentData.amount,
                memo: paymentData.memo
            });
            
            setSuccess('Payment recorded successfully and posted to GL');
            setShowPaymentModal(false);
            fetchInitialData(); // Refresh invoice data
        } catch (err) {
            setError(err.message || 'Failed to record payment');
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleVoidInvoice = async () => {
        if (!window.confirm('Are you sure you want to void this invoice? This action cannot be undone.')) {
            return;
        }
        
        try {
            setLoading(true);
            await invoiceAPI.voidInvoice(id);
            setSuccess('Invoice voided successfully');
            fetchInitialData(); // Refresh data
        } catch (err) {
            setError(err.message || 'Failed to void invoice');
        } finally {
            setLoading(false);
        }
    };

    const { subtotal, discountTotal, taxTotal, totalAmount } = calculateTotals();

    const termsOptions = [
        { value: 'due_on_receipt', label: 'Due on Receipt' },
        { value: 'net_15', label: 'Net 15' },
        { value: 'net_30', label: 'Net 30' },
        { value: 'net_60', label: 'Net 60' },
        { value: '2_10_net_30', label: '2/10 Net 30' },
        { value: 'custom', label: 'Custom' }
    ];

    const paymentMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'check', label: 'Check' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'digital_wallet', label: 'Digital Wallet' }
    ];

    if (loadingInvoice) {
        return (
            <Layout pageTitle="Loading Invoice...">
                <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading invoice...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!invoice) {
        return (
            <Layout pageTitle="Invoice Not Found">
                <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Invoice Not Found</h1>
                        <p className="text-gray-600 mb-4">The invoice you're looking for doesn't exist.</p>
                        <button
                            onClick={() => navigate('/invoice')}
                            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                        >
                            Back to Invoices
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle={`${isReadOnly ? 'View' : 'Edit'} Invoice - ${invoice.invoiceNumber}`}>
            <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">
                            {isReadOnly ? 'View' : 'Edit'} Invoice - {invoice.invoiceNumber}
                        </h1>
                        <div className="flex items-center space-x-4 mt-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                invoice.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                invoice.status === 'void' ? 'bg-gray-100 text-gray-800' :
                                'bg-purple-100 text-purple-800'
                            }`}>
                                {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1).replace('_', ' ')}
                            </span>
                            {invoice.glPostingStatus === 'posted' && (
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                    Posted to GL
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <span>Auto-post to: Accounts Receivable, Sales, COGS, Inventory</span>
                    </div>
                </div>
                
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {success}
                    </div>
                )}

                {/* Status Action Buttons */}
                {invoice?.status !== 'void' && (
                    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-semibold">Invoice Actions</h3>
                                <p className="text-sm text-gray-600">Manage invoice status and accounting</p>
                            </div>
                            <div className="flex space-x-3">
                                {invoice.status === 'draft' && (
                                    <>
                                        <button
                                            onClick={() => handleSubmit('update')}
                                            disabled={loading}
                                            className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
                                        >
                                            {loading ? 'Saving...' : 'Save Draft'}
                                        </button>
                                        <button
                                            onClick={() => handleSubmit('updateAndSend')}
                                            disabled={loading}
                                            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm font-medium"
                                        >
                                            {loading ? 'Posting...' : 'Save and Post to GL'}
                                        </button>
                                    </>
                                )}
                                {(invoice.status === 'sent' || invoice.status === 'partially_paid') && (
                                    <button
                                        onClick={() => setShowPaymentModal(true)}
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
                                    >
                                        Record Payment
                                    </button>
                                )}
                                {invoice.status !== 'void' && (
                                    <button
                                        onClick={handleVoidInvoice}
                                        disabled={loading}
                                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                                    >
                                        {loading ? 'Voiding...' : 'Void Invoice'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Customer Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Customer *
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search customers or add new..."
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                        isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                                    disabled={isReadOnly}
                                />
                                {!isReadOnly && customerSearch && filteredCustomers.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                                        {filteredCustomers.map(customer => (
                                            <div
                                                key={customer._id}
                                                onClick={() => handleCustomerSelect(customer._id)}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b"
                                            >
                                                <div className="font-medium">{customer.name || 'Unnamed Customer'}</div>
                                                {customer.companyName && (
                                                    <div className="text-sm text-gray-600">{customer.companyName}</div>
                                                )}
                                                {customer.email && (
                                                    <div className="text-sm text-gray-500">{customer.email}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {!isReadOnly && customerSearch && !filteredCustomers.some(c => 
                                    c.name.toLowerCase() === customerSearch.toLowerCase() ||
                                    c.email?.toLowerCase() === customerSearch.toLowerCase()
                                ) && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        <div
                                            onClick={() => setShowNewCustomerModal(true)}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b bg-teal-50"
                                        >
                                            <div className="font-medium text-teal-700">
                                                + Add "{customerSearch}" as new customer
                                            </div>
                                            <div className="text-sm text-teal-600">
                                                Create new customer profile
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {formData.customer && (
                                <div className="mt-2 p-2 bg-gray-50 rounded">
                                    <div className="font-medium">
                                        {customers.find(c => c._id === formData.customer)?.name}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {customers.find(c => c._id === formData.customer)?.email}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Customer Email
                            </label>
                            <input
                                type="email"
                                value={formData.customerEmail || ''}
                                onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                }`}
                                placeholder="customer@example.com"
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Invoice Date *
                            </label>
                            <input
                                type="date"
                                value={formData.invoiceDate}
                                onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                }`}
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Due Date *
                            </label>
                            <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                }`}
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Payment Terms
                            </label>
                            <select
                                value={formData.terms}
                                onChange={(e) => setFormData({...formData, terms: e.target.value})}
                                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                }`}
                                disabled={isReadOnly}
                            >
                                {termsOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {formData.terms === 'custom' && (
                                <input
                                    type="text"
                                    value={formData.customTerms}
                                    onChange={(e) => setFormData({...formData, customTerms: e.target.value})}
                                    className={`w-full border rounded px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                        isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                                    placeholder="Enter custom terms"
                                    disabled={isReadOnly}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Line Items Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Line Items</h2>
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={addLine}
                                className="px-3 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center text-sm"
                            >
                                <span className="mr-1">+</span> Add Item
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product/Service *
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Qty
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rate
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Discount %
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tax %
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    {!isReadOnly && (
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {formData.lines.map((line, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="relative">
                                                <select
                                                    value={line.product}
                                                    onChange={(e) => updateLine(index, 'product', e.target.value)}
                                                    className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                        isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                                    required
                                                    disabled={isReadOnly}
                                                >
                                                    <option value="">Select Product</option>
                                                    {filteredProducts.map(product => (
                                                        <option key={product._id} value={product._id}>
                                                            {product.name} - ₱{product.salePrice}
                                                            {product.trackInventory && ` (Stock: ${product.quantityOnHand})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="text"
                                                value={line.description}
                                                onChange={(e) => updateLine(index, 'description', e.target.value)}
                                                className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                                placeholder="Item description"
                                                disabled={isReadOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={line.quantity}
                                                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                                                className={`w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                                disabled={isReadOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={line.unitPrice}
                                                onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                                                className={`w-24 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                                disabled={isReadOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={line.discount}
                                                onChange={(e) => updateLine(index, 'discount', e.target.value)}
                                                className={`w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                                disabled={isReadOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={line.taxRate}
                                                onChange={(e) => updateLine(index, 'taxRate', e.target.value)}
                                                className={`w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                                                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                                disabled={isReadOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                                            ₱{line.lineTotal.toFixed(2)}
                                        </td>
                                        {!isReadOnly && (
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => removeLine(index)}
                                                    disabled={formData.lines.length <= 1}
                                                    className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed text-sm"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals Summary */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-end">
                            <div className="w-64 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-medium">₱{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-red-600">-₱{discountTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Tax:</span>
                                    <span className="font-medium">₱{taxTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                    <span className="text-lg font-bold">Total:</span>
                                    <span className="text-lg font-bold">₱{totalAmount.toFixed(2)}</span>
                                </div>
                                {invoice.amountDue !== undefined && (
                                    <div className="flex justify-between border-t pt-2">
                                        <span className="text-lg font-bold text-green-600">Amount Due:</span>
                                        <span className="text-lg font-bold text-green-600">₱{invoice.amountDue.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Memo Field */}
                    <div className="mt-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                            Memo / Notes
                        </label>
                        <textarea
                            value={formData.memo}
                            onChange={(e) => setFormData({...formData, memo: e.target.value})}
                            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            rows={3}
                            placeholder="Additional notes or terms for the customer..."
                            disabled={isReadOnly}
                        />
                    </div>
                </div>

                {/* Accounting Information */}
                {invoice.glPostingStatus === 'posted' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                        <h3 className="text-lg font-semibold text-green-800 mb-3">Accounting Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-green-700 mb-2">Journal Entries:</h4>
                                <div className="text-sm space-y-2">
                                    {invoice.salesJournalEntry && (
                                        <div>
                                            <span className="font-medium">Sales Entry:</span> 
                                            <span className="ml-2 text-green-600">Posted</span>
                                        </div>
                                    )}
                                    {invoice.cogsJournalEntry && (
                                        <div>
                                            <span className="font-medium">COGS Entry:</span> 
                                            <span className="ml-2 text-green-600">Posted</span>
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-600">
                                        GL Batch: {invoice.glBatchNumber}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-green-700 mb-2">Payment Status:</h4>
                                <div className="text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span>Invoice Total:</span>
                                        <span className="font-medium">₱{invoice.totalAmount?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Amount Paid:</span>
                                        <span className="font-medium text-green-600">
                                            ₱{((invoice.totalAmount || 0) - (invoice.amountDue || 0)).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-t pt-1">
                                        <span>Balance Due:</span>
                                        <span className="font-medium">
                                            ₱{(invoice.amountDue || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/invoice')}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {invoice?.status === 'draft' && (
                        <>
                            <button
                                type="button"
                                onClick={() => handleSubmit('update')}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Draft'}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSubmit('updateAndSend')}
                                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                disabled={loading}
                            >
                                {loading ? 'Posting...' : 'Save and Post to GL'}
                            </button>
                        </>
                    )}
                </div>

                {/* New Customer Modal */}
                {showNewCustomerModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold">Add New Customer</h3>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Customer Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newCustomerData.name}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            name: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Enter customer name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={newCustomerData.email}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            email: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="customer@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={newCustomerData.phone}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            phone: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Promotional Preferences
                                    </label>
                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newCustomerData.promoOptIn.email}
                                                onChange={(e) => setNewCustomerData({
                                                    ...newCustomerData,
                                                    promoOptIn: {
                                                        ...newCustomerData.promoOptIn,
                                                        email: e.target.checked
                                                    }
                                                })}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">Email</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newCustomerData.promoOptIn.sms}
                                                onChange={(e) => setNewCustomerData({
                                                    ...newCustomerData,
                                                    promoOptIn: {
                                                        ...newCustomerData.promoOptIn,
                                                        sms: e.target.checked
                                                    }
                                                })}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">SMS</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowNewCustomerModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    disabled={creatingCustomer}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCreateCustomer}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm font-medium"
                                    disabled={creatingCustomer}
                                >
                                    {creatingCustomer ? 'Creating...' : 'Create Customer'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payment Modal */}
                {showPaymentModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold">Record Payment</h3>
                                <p className="text-sm text-gray-600 mt-1">Invoice #{invoice.invoiceNumber}</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Date *
                                    </label>
                                    <input
                                        type="date"
                                        value={paymentData.paymentDate}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            paymentDate: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Method *
                                    </label>
                                    <select
                                        value={paymentData.paymentMethod}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            paymentMethod: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    >
                                        {paymentMethods.map(method => (
                                            <option key={method.value} value={method.value}>
                                                {method.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reference Number
                                    </label>
                                    <input
                                        type="text"
                                        value={paymentData.referenceNumber}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            referenceNumber: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Check #, Transaction ID, etc."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Amount *
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={paymentData.amount}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            amount: parseFloat(e.target.value) || 0
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Amount Due: ₱{invoice.amountDue?.toFixed(2)}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Memo
                                    </label>
                                    <textarea
                                        value={paymentData.memo}
                                        onChange={(e) => setPaymentData({
                                            ...paymentData,
                                            memo: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        rows={2}
                                        placeholder="Payment notes..."
                                    />
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                    <h4 className="font-medium text-blue-800 text-sm mb-2">GL Impact:</h4>
                                    <div className="text-xs space-y-1 text-blue-700">
                                        <div>Dr Cash/Bank (based on payment method)</div>
                                        <div>Cr Accounts Receivable</div>
                                        <div className="italic">Auto-posted to General Ledger</div>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowPaymentModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    disabled={processingPayment}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleMarkAsPaid}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                                    disabled={processingPayment}
                                >
                                    {processingPayment ? 'Processing...' : 'Record Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default EditInvoicePage;